// Per-ASIN weekly history panel for the War Room row expansion (Task 7). Renders the
// past ~26 weeks (180 days) for a single ASIN: avg FC count, avg units held, units sold,
// page-view-weighted BB%, and whether a MAP breaker was active that week. Data comes from
// ScorecardService.getAsinHistory() ascending by week; this panel shows newest week first.
//
// Task 11: also renders an "FC Coverage" section below the weekly history, fetching
// GET /api/war-room/fc-analysis/{asin} itself (the row's <tr> — and this panel with it — is
// unmounted on collapse, so a module-level cache keyed by asin avoids a redundant fetch when a
// row is re-expanded).
import React, { useEffect, useState } from 'react';
import ApiClient from '../../utils/ApiClient';
import FcCoverageTable from './FcCoverageTable';

const fmtNum = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v}%`);

const fcAnalysisCache = {};

function useFcAnalysis(asin) {
    const [state, setState] = useState(() => (asin && fcAnalysisCache[asin]) || { status: 'loading', rows: [] });

    useEffect(() => {
        if (!asin) return;
        if (fcAnalysisCache[asin]) {
            setState(fcAnalysisCache[asin]);
            return;
        }
        setState({ status: 'loading', rows: [] });
        ApiClient.get(`/api/war-room/fc-analysis/${asin}`)
            .then((response) => {
                const next = { status: 'loaded', rows: response.data || [] };
                fcAnalysisCache[asin] = next;
                setState(next);
            })
            .catch((err) => {
                console.error('Error fetching FC analysis:', err);
                const next = { status: 'error', rows: [] };
                fcAnalysisCache[asin] = next;
                setState(next);
            });
    }, [asin]);

    return state;
}

function bbPctClass(v) {
    if (v === null || v === undefined) return 'text-gray-400';
    if (v >= 70) return 'text-green-600 font-semibold';
    if (v >= 30) return 'text-amber-600 font-semibold';
    return 'text-red-600 font-semibold';
}

// Transit-time ramp: "Ours 6.3d · MP 6.7d". Either side renders '—' when that seller had no
// qualifying (New/Amazon.com) scrape data for this ASIN that week; the whole cell is '—' when
// neither side has data.
function promiseText(row) {
    const our = row.avgOurPromise;
    const mp = row.avgMpPromise;
    if (our == null && mp == null) return '—';
    return `Ours ${our != null ? `${our}d` : '—'} · MP ${mp != null ? `${mp}d` : '—'}`;
}

function promiseClass(row) {
    const our = row.avgOurPromise;
    const mp = row.avgMpPromise;
    if (our == null || mp == null) return 'text-gray-400';
    if (our < mp) return 'text-green-600 font-semibold';
    if (our > mp) return 'text-red-600 font-semibold';
    return 'text-gray-700';
}

function CheatedCell({ row }) {
    if (!row.cheated) {
        return <span className="text-gray-400">—</span>;
    }
    const days = row.violDays != null ? row.violDays : 0;
    const worst = row.worstPctUnder != null ? `${row.worstPctUnder}%` : '—';
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-300">
            {days} day{days === 1 ? '' : 's'} &middot; worst {worst} under
        </span>
    );
}

function WeeklyHistoryTable({ status, rows }) {
    if (status === 'loading') {
        return (
            <div className="flex items-center justify-center py-6 text-gray-400">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="ml-2 text-sm">Loading weekly history…</span>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="py-4 text-center text-sm text-red-600">
                Failed to load weekly history. Please try again.
            </div>
        );
    }

    const weeks = (rows || []).slice().reverse(); // newest week at the top

    if (weeks.length === 0) {
        return (
            <div className="py-4 text-center text-sm text-gray-500">No weekly history for this ASIN in the last 180 days.</div>
        );
    }

    return (
        <div className="bg-gray-50 border border-gray-200 rounded-md overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-100">
                    <tr className="text-left font-semibold text-gray-500 uppercase tracking-wide">
                        <th className="px-3 py-1.5">Week</th>
                        <th className="px-3 py-1.5 text-right">Avg FCs</th>
                        <th className="px-3 py-1.5 text-right">Avg Units</th>
                        <th className="px-3 py-1.5 text-right">Sold</th>
                        <th className="px-3 py-1.5 text-right">BB %</th>
                        <th className="px-3 py-1.5 text-right" title="Weekly average promised delivery days, NEW FBA offers only (not demand-weighted)">Transit</th>
                        <th className="px-3 py-1.5">Cheated</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                    {weeks.map((row) => (
                        <tr key={row.week}>
                            <td className="px-3 py-1.5 font-mono">{row.week}</td>
                            <td className="px-3 py-1.5 text-right">{fmtNum(row.avgFcs)}</td>
                            <td className="px-3 py-1.5 text-right">{fmtNum(row.avgUnits)}</td>
                            <td className="px-3 py-1.5 text-right">{fmtNum(row.unitsSold)}</td>
                            <td className={`px-3 py-1.5 text-right ${bbPctClass(row.pvBbPct)}`}>{fmtPct(row.pvBbPct)}</td>
                            <td className={`px-3 py-1.5 text-right whitespace-nowrap ${promiseClass(row)}`}>{promiseText(row)}</td>
                            <td className="px-3 py-1.5"><CheatedCell row={row} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function AsinHistoryPanel({ status, rows, asin, dwCov1dPct, dwCov2dPct, dwCov1dEffPct, dwCov2dEffPct }) {
    const fcAnalysis = useFcAnalysis(asin);

    return (
        <div className="space-y-3">
            <WeeklyHistoryTable status={status} rows={rows} />

            <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">FC Coverage</div>
                <FcCoverageTable
                    status={fcAnalysis.status}
                    rows={fcAnalysis.rows}
                    dwCov1dPct={dwCov1dPct}
                    dwCov2dPct={dwCov2dPct}
                    dwCov1dEffPct={dwCov1dEffPct}
                    dwCov2dEffPct={dwCov2dEffPct}
                />
            </div>
        </div>
    );
}

export default AsinHistoryPanel;
